import vestiaireConfig from '../data/vestiaire-config.json';

const vestiaireColorLabels = vestiaireConfig.colors.map((color) => color.label);

// Canonical color palette — union of all platform palettes plus common shades
export const CANONICAL_COLORS = [
  'Black', 'White', 'Gray', 'Silver', 'Gold',
  'Red', 'Burgundy', 'Pink', 'Hot Pink', 'Coral',
  'Orange', 'Apricot', 'Yellow', 'Mustard',
  'Lime', 'Green', 'Dark Green', 'Mint', 'Teal', 'Turquoise',
  'Light Blue', 'Blue', 'Navy',
  'Purple', 'Lavender', 'Lilac', 'Rose',
  'Brown', 'Tan', 'Beige', 'Cream', 'Khaki',
  'Multicolor', 'Metallic', 'Transparent',
];

export const PLATFORMS = {
  VINTED: 'vinted',
  DEPOP: 'depop',
  GRAILED: 'grailed',
  WHATNOT: 'whatnot',
  OFFERUP: 'offerup',
  VESTIAIRE: 'vestiaire',
  POSHMARK: 'poshmark',
  EBAY: 'ebay',
  ETSY: 'etsy',
};

// ── Canonical → Vinted numeric IDs ──────────────────────────────────────────
const TO_VINTED = {
  Black: 1, Brown: 2, Gray: 3, Grey: 3, Beige: 4, Pink: 5, Purple: 6, Red: 7,
  Yellow: 8, Blue: 9, Green: 10, Orange: 11, White: 12, Silver: 13, Gold: 14,
  Multicolor: 15, Multicolour: 15, Khaki: 16, Turquoise: 17, Teal: 17,
  Cream: 20, Apricot: 21, Coral: 22, Burgundy: 23, Rose: 24, 'Hot Pink': 24,
  Lavender: 25, Lilac: 25, 'Light Blue': 26, Navy: 27, 'Dark Green': 28,
  Mustard: 29, Mint: 30, Metallic: 13, Tan: 4, Lime: 10, Transparent: null,
};

// ── Canonical → Depop lowercase strings ─────────────────────────────────────
// Valid Depop color IDs: black, grey, white, brown, tan, cream, yellow, red,
// burgundy, orange, pink, purple, blue, navy, green, khaki, multi, silver, gold
const TO_DEPOP = {
  Black: 'black', White: 'white', Gray: 'grey', Grey: 'grey', Red: 'red',
  Pink: 'pink', 'Hot Pink': 'pink', Blue: 'blue', Navy: 'navy',
  'Light Blue': 'blue', Green: 'green', 'Dark Green': 'green',
  Yellow: 'yellow', Orange: 'orange', Brown: 'brown', Tan: 'tan',
  Beige: 'cream', Cream: 'cream', Purple: 'purple', Lavender: 'purple',
  Lilac: 'purple', Gold: 'gold', Silver: 'silver', Multicolor: 'multi',
  Teal: 'blue', Turquoise: 'blue', Khaki: 'khaki', Burgundy: 'burgundy',
  Rose: 'pink', Coral: 'orange', Mint: 'green', Mustard: 'yellow',
  Apricot: 'orange', Lime: 'green', Metallic: 'silver',
};

// ── Canonical → Etsy nearest color name (fallback when exact match fails) ────
// Etsy palette: Beige, Black, Blue, Bronze, Brown, Clear, Copper, Gold, Gray,
//               Green, Orange, Pink, Purple, Rainbow, Red, Rose gold, Silver, White, Yellow
export const TO_ETSY_FALLBACK = {
  Black: 'Black', White: 'White', Gray: 'Gray', Silver: 'Silver', Gold: 'Gold',
  Red: 'Red', Burgundy: 'Red', Pink: 'Pink', 'Hot Pink': 'Pink', Rose: 'Pink',
  Coral: 'Orange', Orange: 'Orange', Apricot: 'Orange',
  Yellow: 'Yellow', Mustard: 'Yellow',
  Lime: 'Green', Green: 'Green', 'Dark Green': 'Green', Mint: 'Green', Khaki: 'Green',
  Teal: 'Blue', Turquoise: 'Blue', 'Light Blue': 'Blue', Blue: 'Blue', Navy: 'Blue',
  Purple: 'Purple', Lavender: 'Purple', Lilac: 'Purple',
  Brown: 'Brown', Tan: 'Beige', Beige: 'Beige', Cream: 'Beige',
  Multicolor: 'Rainbow', Metallic: 'Silver', Transparent: 'Clear',
};

// ── Canonical → Poshmark fixed 15-color palette ──────────────────────────────
const TO_POSHMARK = {
  Black: 'Black', White: 'White', Gray: 'Gray', Grey: 'Gray',
  Silver: 'Silver', Gold: 'Gold', Red: 'Red', Burgundy: 'Red',
  Pink: 'Pink', 'Hot Pink': 'Pink', Rose: 'Pink', Coral: 'Red',
  Orange: 'Orange', Apricot: 'Orange', Yellow: 'Yellow', Mustard: 'Yellow',
  Lime: 'Green', Green: 'Green', 'Dark Green': 'Green', Teal: 'Blue',
  Mint: 'Green', Turquoise: 'Blue', 'Light Blue': 'Blue', Blue: 'Blue',
  Navy: 'Blue', Purple: 'Purple', Lavender: 'Purple', Lilac: 'Purple',
  Brown: 'Brown', Tan: 'Tan', Beige: 'Tan', Cream: 'Cream', Khaki: 'Tan',
  Multicolor: null, Metallic: 'Silver', Transparent: null,
};

// ── Reverse lookup tables ────────────────────────────────────────────────────
// Vinted ID → canonical (first canonical that maps to this ID wins)
// Grailed's colours, in the order its sell form lists them. A listing sends one of these words
// (`traits: [{ name: 'color', value: 'White' }]`); Grailed reads it back in lower case.
export const GRAILED_COLORS = [
  'Black', 'White', 'Gray', 'Brown', 'Beige', 'Yellow', 'Red', 'Orange', 'Pink', 'Purple', 'Blue',
  'Green', 'Multi', 'Silver', 'Gold',
];

const TO_GRAILED = {
  Black: 'Black', White: 'White', Gray: 'Gray', Grey: 'Gray', Silver: 'Silver', Gold: 'Gold',
  Red: 'Red', Burgundy: 'Red', Pink: 'Pink', 'Hot Pink': 'Pink', Rose: 'Pink',
  Orange: 'Orange', Coral: 'Orange', Apricot: 'Orange', Yellow: 'Yellow', Mustard: 'Yellow',
  Green: 'Green', Lime: 'Green', 'Dark Green': 'Green', Mint: 'Green',
  Blue: 'Blue', 'Light Blue': 'Blue', Navy: 'Blue', Teal: 'Blue', Turquoise: 'Blue',
  Purple: 'Purple', Lavender: 'Purple', Lilac: 'Purple',
  Brown: 'Brown', Beige: 'Beige', Tan: 'Beige', Cream: 'Beige', Khaki: 'Beige',
  Multicolor: 'Multi', Metallic: 'Silver', Transparent: null,
};

const FROM_GRAILED = {
  black: 'Black', white: 'White', gray: 'Gray', brown: 'Brown', beige: 'Beige', yellow: 'Yellow',
  red: 'Red', orange: 'Orange', pink: 'Pink', purple: 'Purple', blue: 'Blue', green: 'Green',
  multi: 'Multicolor', silver: 'Silver', gold: 'Gold',
};

// Whatnot's colours: the values of its shared Color attribute (`universal.color`), which the fashion, bags,
// beauty and home categories ask. Keep in sync with server/src/utils/color-mapper.js.
export const WHATNOT_COLORS = ['Black', 'White', 'Gray', 'Beige', 'Cream', 'Brown', 'Blue', 'Navy', 'Red', 'Pink', 'Purple',
  'Orange', 'Yellow', 'Green', 'Gold', 'Silver', 'Rose Gold', 'Clear', 'Multicolor'];

// Voolist's colours that are not Whatnot words, as the Whatnot colour nearest each. The others are
// Whatnot's own words already and are sent as they are.
const TO_WHATNOT = {
  Grey: 'Gray', Burgundy: 'Red', 'Hot Pink': 'Pink', Rose: 'Pink', Coral: 'Orange', Apricot: 'Orange',
  Mustard: 'Yellow', Lime: 'Green', 'Dark Green': 'Green', Mint: 'Green', 'Light Blue': 'Blue', Teal: 'Blue',
  Turquoise: 'Blue', Lavender: 'Purple', Lilac: 'Purple', Tan: 'Beige', Khaki: 'Beige', Metallic: 'Silver',
  Transparent: 'Clear',
};

// Keyed in lower case: Whatnot's colour attributes do not all capitalise alike.
const FROM_WHATNOT = {
  black: 'Black', white: 'White', gray: 'Gray', beige: 'Beige', cream: 'Cream', brown: 'Brown', blue: 'Blue',
  navy: 'Navy', red: 'Red', pink: 'Pink', purple: 'Purple', orange: 'Orange', yellow: 'Yellow', green: 'Green',
  gold: 'Gold', silver: 'Silver', 'rose gold': 'Gold', clear: 'Transparent', multicolor: 'Multicolor',
};

/**
 * A colour in Whatnot's words: one of Whatnot's own colours, in any case, else Voolist's colour translated
 * (TO_WHATNOT). Null for a colour that is neither.
 * @param {string} color
 * @returns {string|null}
 */
function toWhatnotColor(color) {
  const text = String(color).trim();
  const own = WHATNOT_COLORS.find(name => name.toLowerCase() === text.toLowerCase());
  if (own) return own;
  const canonical = Object.keys(TO_WHATNOT).find(name => name.toLowerCase() === text.toLowerCase());
  return canonical ? TO_WHATNOT[canonical] : null;
}

/**
 * A colour in OfferUp's words: one of OfferUp's own colours, in any case, else Voolist's colour translated
 * (TO_OFFERUP). Null for a colour that is neither, or that OfferUp has no word for (Transparent).
 *
 * @param {string} color
 * @returns {string|null}
 */
function toOfferupColor(color) {
  const text = String(color).trim();
  const own = OFFERUP_COLORS.find(name => name.toLowerCase() === text.toLowerCase());
  if (own) return own;
  const canonical = Object.keys(TO_OFFERUP).find(name => name.toLowerCase() === text.toLowerCase());
  return canonical ? TO_OFFERUP[canonical] : null;
}

/**
 * A colour in Vestiaire's words: one of Vestiaire's own colours, in any case, else Voolist's colour translated
 * (TO_VESTIAIRE). Null for a colour that is neither.
 *
 * @param {string} color
 * @returns {string|null}
 */
function toVestiaireColor(color) {
  const text = String(color).trim();
  const own = VESTIAIRE_COLORS.find(name => name.toLowerCase() === text.toLowerCase());
  if (own) return own;
  const canonical = Object.keys(TO_VESTIAIRE).find(name => name.toLowerCase() === text.toLowerCase());
  return canonical ? TO_VESTIAIRE[canonical] : null;
}

// OfferUp's colours: the values of the Color detail its clothing, shoes and home categories ask
// (GetCategoryAttributes; OFFERUP_API_REFERENCE.md §5). A few categories offer a slightly different list, so
// the extension sends the word only where the category's own list has it or takes other words.
export const OFFERUP_COLORS = ['Black', 'White', 'Gray', 'Brown', 'Beige', 'Red', 'Pink', 'Orange', 'Yellow', 'Green', 'Blue',
  'Purple', 'Silver', 'Gold', 'Multicolor'];

// Voolist's colours that are not OfferUp words, as the OfferUp colour nearest each (as on Grailed and Whatnot).
// The others are OfferUp's own words already and are sent as they are. OfferUp has no word for Transparent.
const TO_OFFERUP = {
  Grey: 'Gray', Burgundy: 'Red', 'Hot Pink': 'Pink', Rose: 'Pink', Coral: 'Orange', Apricot: 'Orange',
  Mustard: 'Yellow', Lime: 'Green', 'Dark Green': 'Green', Mint: 'Green', 'Light Blue': 'Blue', Navy: 'Blue',
  Teal: 'Blue', Turquoise: 'Blue', Lavender: 'Purple', Lilac: 'Purple', Tan: 'Beige', Cream: 'Beige', Khaki: 'Beige',
  Metallic: 'Silver', Transparent: null,
};

// Keyed in lower case, so a colour read back in any case is recognised.
const FROM_OFFERUP = Object.fromEntries(OFFERUP_COLORS.map((color) => [color.toLowerCase(), color]));

// Vestiaire's 24 colours (data/vestiaire-config.json), the same in every category its form was read in.
const VESTIAIRE_COLORS = vestiaireColorLabels;

// Voolist's colours that are not Vestiaire words, as the Vestiaire colour nearest each (as on Grailed, Whatnot and
// OfferUp). The others are Vestiaire's own words already and are sent as they are.
const TO_VESTIAIRE = {
  Gray: 'Grey', 'Hot Pink': 'Pink', Rose: 'Pink', Coral: 'Orange', Apricot: 'Orange', Mustard: 'Yellow',
  Lime: 'Green', 'Dark Green': 'Green', Mint: 'Green', Teal: 'Turquoise', 'Light Blue': 'Blue',
  Lavender: 'Purple', Lilac: 'Purple', Tan: 'Camel', Cream: 'Ecru', Multicolor: 'Multicolour', Transparent: 'Other',
};

// Vestiaire's colours read back as Voolist's, keyed in lower case. Anthracite is a dark grey; "Other" says nothing.
const FROM_VESTIAIRE = Object.fromEntries([
  ...VESTIAIRE_COLORS.filter((color) => color !== 'Other').map((color) => [color.toLowerCase(), color]),
  ['grey', 'Gray'], ['anthracite', 'Gray'], ['camel', 'Tan'], ['ecru', 'Cream'], ['multicolour', 'Multicolor'],
]);

const FROM_VINTED = {};
Object.entries(TO_VINTED).forEach(([canonical, id]) => {
  if (id != null && !(id in FROM_VINTED)) FROM_VINTED[id] = canonical;
});

// Depop lowercase → canonical (first canonical wins)
const FROM_DEPOP = {};
Object.entries(TO_DEPOP).forEach(([canonical, depopVal]) => {
  if (!(depopVal in FROM_DEPOP)) FROM_DEPOP[depopVal] = canonical;
});

// Poshmark palette string → canonical (first canonical wins)
const FROM_POSHMARK = {};
Object.entries(TO_POSHMARK).forEach(([canonical, poshmarkVal]) => {
  if (poshmarkVal != null && !(poshmarkVal in FROM_POSHMARK)) FROM_POSHMARK[poshmarkVal] = canonical;
});

// eBay localized color option values → canonical English
// Verified against eBay Taxonomy API (getItemAspectsForCategory) per marketplace tree.
// Values that match English canonically (Beige, Orange) are omitted — passthrough via || String(value).
const FROM_EBAY_LOCALIZED_COLORS = {
  // German (DE, AT, CH)
  'Schwarz': 'Black', 'Blau': 'Blue', 'Braun': 'Brown', 'Grau': 'Gray',
  'Grün': 'Green', 'Elfenbeinfarben': 'Ivory', 'Mehrfarbig': 'Multicolor',
  'Rosa': 'Pink', 'Lila': 'Purple', 'Rot': 'Red', 'Silber': 'Silver',
  'Weiß': 'White', 'Gelb': 'Yellow',
  // French (FR)
  'Noir': 'Black', 'Bleu': 'Blue', 'Marron': 'Brown', 'Doré': 'Gold',
  'Gris': 'Gray', 'Vert': 'Green', 'Ivoire': 'Ivory', 'Multicouleur': 'Multicolor',
  'Rose': 'Pink', 'Violet': 'Purple', 'Rouge': 'Red', 'Argenté': 'Silver',
  'Blanc': 'White', 'Jaune': 'Yellow',
  // Italian (IT)
  'Nero': 'Black', 'Blu': 'Blue', 'Marrone': 'Brown', 'Oro': 'Gold',
  'Grigio': 'Gray', 'Verde': 'Green', 'Avorio': 'Ivory', 'Multicolore': 'Multicolor',
  'Arancione': 'Orange', 'Viola': 'Purple', 'Rosso': 'Red', 'Argento': 'Silver',
  'Bianco': 'White', 'Giallo': 'Yellow', 'Transparente': 'Transparent',
  // Spanish (ES)
  'Negro': 'Black', 'Azul': 'Blue', 'Marrón': 'Brown',
  'Morado': 'Purple', 'Rojo': 'Red', 'Plata': 'Silver', 'Blanco': 'White',
  'Amarillo': 'Yellow', 'Naranja': 'Orange',
  // Dutch (NL, NL-BE)
  'Zwart': 'Black', 'Blauw': 'Blue', 'Bruin': 'Brown',
  'Goudkleurig': 'Gold', 'Goudkleur': 'Gold',
  'Grijs': 'Gray', 'Groen': 'Green', 'Ivoor': 'Ivory', 'Meerkleurig': 'Multicolor',
  'Oranje': 'Orange', 'Roze': 'Pink', 'Paars': 'Purple', 'Rood': 'Red',
  'Zilverkleurig': 'Silver', 'Zilverkleur': 'Silver',
  'Wit': 'White', 'Geel': 'Yellow', 'Transparant': 'Transparent',
};

// Etsy name → canonical (most are direct matches; 5 need explicit mapping)
const FROM_ETSY = {
  Beige: 'Beige', Black: 'Black', Blue: 'Blue', Brown: 'Brown',
  Gold: 'Gold', Gray: 'Gray', Green: 'Green', Orange: 'Orange',
  Pink: 'Pink', Purple: 'Purple', Red: 'Red', Silver: 'Silver',
  White: 'White', Yellow: 'Yellow',
  Bronze: 'Brown', Clear: 'Transparent', Copper: 'Gold',
  Rainbow: 'Multicolor', 'Rose gold': 'Pink',
};

// ── Platform color validators / mappers ──────────────────────────────────────

const VALID_DEPOP_IDS = new Set([
  'black', 'grey', 'white', 'brown', 'tan', 'cream', 'yellow', 'red',
  'burgundy', 'orange', 'pink', 'purple', 'blue', 'navy', 'green',
  'khaki', 'multi', 'silver', 'gold',
]);

const VALID_POSHMARK_COLORS = new Set([
  'Black', 'Blue', 'Brown', 'Cream', 'Gold', 'Gray', 'Green',
  'Orange', 'Pink', 'Purple', 'Red', 'Silver', 'Tan', 'White', 'Yellow',
]);

/**
 * Map any value to a valid platform-specific color.
 * Handles already-valid IDs, canonical names, and case variants.
 * @param {string} value
 * @param {string} platform - 'depop', 'poshmark', or any PLATFORMS value
 * @returns {string|number|null}
 */
export function mapToValidColor(value, platform) {
  if (!value) return null;
  // Normalize object-shaped values (e.g. Poshmark API: {name, rgb, message_id})
  const normalized = (typeof value === 'object' && value !== null)
    ? (value.name || value.message_id || null)
    : value;
  if (!normalized) return null;
  const str = String(normalized);
  const lower = str.toLowerCase();
  if (platform === 'depop') {
    if (VALID_DEPOP_IDS.has(lower)) return lower;
    const cap = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    return TO_DEPOP[str] ?? TO_DEPOP[cap] ?? null;
  }
  if (platform === 'grailed') {
    const grailedColor = GRAILED_COLORS.find(color => color.toLowerCase() === lower);
    if (grailedColor) return grailedColor;
    const cap = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    return TO_GRAILED[str] ?? TO_GRAILED[cap] ?? null;
  }
  if (platform === 'whatnot') return toWhatnotColor(str);
  if (platform === 'offerup') return toOfferupColor(str);
  if (platform === 'poshmark') {
    if (VALID_POSHMARK_COLORS.has(str)) return str;
    const cap = str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    return TO_POSHMARK[str] ?? TO_POSHMARK[cap] ?? null;
  }
  return toColor(str, platform) ?? str;
}

/**
 * Convert a canonical color name to a platform-specific value.
 * @param {string} canonicalColor
 * @param {string} platform  — one of PLATFORMS values
 * @returns {number|string|null}
 */
export function toColor(canonicalColor, platform) {
  if (!canonicalColor) return null;
  switch (platform) {
    case PLATFORMS.VINTED: {
      const id = TO_VINTED[canonicalColor];
      return id !== undefined ? id : null;
    }
    case PLATFORMS.DEPOP:
      return TO_DEPOP[canonicalColor] ?? null;
    case PLATFORMS.POSHMARK:
      return TO_POSHMARK[canonicalColor] ?? null;
    case PLATFORMS.GRAILED:
      return TO_GRAILED[canonicalColor] ?? null;
    case PLATFORMS.WHATNOT:
      return toWhatnotColor(canonicalColor);
    case PLATFORMS.OFFERUP:
      return toOfferupColor(canonicalColor);
    case PLATFORMS.VESTIAIRE:
      return toVestiaireColor(canonicalColor);
    case PLATFORMS.EBAY:
    case PLATFORMS.ETSY:
      return canonicalColor;
    default:
      return null;
  }
}

/**
 * Convert a platform-specific color value back to a canonical name.
 * @param {string} platformValue  — numeric string for Vinted, lowercase string for Depop, etc.
 * @param {string} platform
 * @returns {string|null}
 */
/**
 * Find the correct localized eBay color option for a canonical English color name.
 * Handles both English eBay (direct match) and non-English (reverse lookup via
 * FROM_EBAY_LOCALIZED_COLORS).
 * @param {string} canonicalColor — English canonical color (e.g., "Black")
 * @param {Array} aspectValues — eBay field's aspectValues array [{localizedValue: "Schwarz"}, ...]
 * @returns {string|null} The matching localized value, or null if no match
 */
export function matchEbayColor(canonicalColor, aspectValues) {
  if (!canonicalColor || !aspectValues?.length) return null;
  const lower = canonicalColor.toLowerCase();
  // Direct match (English eBay or identical value like "Beige", "Orange")
  const direct = aspectValues.find(v => v.localizedValue.toLowerCase() === lower);
  if (direct) return direct.localizedValue;
  // Reverse lookup: find the option whose canonical English form matches
  const reverse = aspectValues.find(v => {
    const canonical = FROM_EBAY_LOCALIZED_COLORS[v.localizedValue];
    return canonical && canonical.toLowerCase() === lower;
  });
  return reverse ? reverse.localizedValue : null;
}

export function fromColor(platformValue, platform) {
  if (platformValue == null) return null;
  // Normalize object-shaped values (e.g. Poshmark API: {name, rgb, message_id})
  const value = (typeof platformValue === 'object' && platformValue !== null)
    ? (platformValue.name || platformValue.message_id || null)
    : platformValue;
  if (value == null) return null;
  switch (platform) {
    case PLATFORMS.VINTED:
      return FROM_VINTED[Number(value)] ?? null;
    case PLATFORMS.DEPOP:
      return FROM_DEPOP[String(value).toLowerCase()] ?? null;
    case PLATFORMS.POSHMARK:
      return FROM_POSHMARK[String(value)] ?? null;
    case PLATFORMS.GRAILED:
      return FROM_GRAILED[String(value).trim().toLowerCase()] ?? null;
    case PLATFORMS.WHATNOT:
      return FROM_WHATNOT[String(value).trim().toLowerCase()] ?? null;
    case PLATFORMS.OFFERUP:
      return FROM_OFFERUP[String(value).trim().toLowerCase()] ?? null;
    case PLATFORMS.VESTIAIRE:
      return FROM_VESTIAIRE[String(value).trim().toLowerCase()] ?? null;
    case PLATFORMS.EBAY:
      return FROM_EBAY_LOCALIZED_COLORS[String(value)] || String(value);
    case PLATFORMS.ETSY:
      return FROM_ETSY[String(value)] ?? String(value);
    default:
      return null;
  }
}
